import { parse, differenceInMonths, differenceInYears, isValid } from 'date-fns'

export interface CalculationParams {
  salario: number
  dataInicio: string // ISO format
  dataRescisao: string // dd/MM/yyyy format
  tipoAviso: 'TRABALHADO' | 'INDENIZADO' | ''
  semJustaCausa: boolean
}

export interface FinancialSummary {
  salarioProporcional: number
  avisoPrevio: number
  fgts: number
  decimoTerceiroProporcional: number
  feriasProporcionais: number
  total: number
}

/**
 * Calcula o salário proporcional baseado na data de rescisão
 */
export const calculateProportionalSalary = (
  salario: number,
  dataInicio: string,
  dataRescisao: string
): number => {
  if (!dataRescisao || !dataInicio) return 0

  const inicioDate = parse(dataInicio.substring(0, 10), 'yyyy-MM-dd', new Date())
  const rescisaoDate = parse(dataRescisao, 'dd/MM/yyyy', new Date())

  if (!isValid(inicioDate) || !isValid(rescisaoDate)) return 0

  // Calcula dias trabalhados no mês da rescisão
  const diasNoMes = new Date(rescisaoDate.getFullYear(), rescisaoDate.getMonth() + 1, 0).getDate()
  const diasTrabalhados = rescisaoDate.getDate()

  // Salário proporcional = (salário / dias do mês) * dias trabalhados
  const salarioProporcional = (salario / diasNoMes) * diasTrabalhados  

  return Math.round(salarioProporcional * 100) / 100
}

/**
 * Calcula o valor do aviso prévio
 */
export const calculateAvisoPrevio = (
  salario: number,
  dataInicio: string,
  dataRescisao: string,
  tipoAviso: 'TRABALHADO' | 'INDENIZADO' | ''
): { noticePayment: number; noticeDays: number } => {
  if (!tipoAviso || tipoAviso === 'TRABALHADO') return { noticePayment: 0, noticeDays: 0 }

  if (!dataInicio || !dataRescisao) return { noticePayment: 0, noticeDays: 0 }

  const inicioDate = parse(dataInicio.substring(0, 10), 'yyyy-MM-dd', new Date())
  const rescisaoDate = parse(dataRescisao, 'dd/MM/yyyy', new Date())

  if (!isValid(inicioDate) || !isValid(rescisaoDate)) return { noticePayment: 0, noticeDays: 0 }

  const completeYears = differenceInYears(rescisaoDate, inicioDate)
  const noticeDays = Math.min(30 + (3 * completeYears), 60)
  const noticePayment = (salario / 30) * noticeDays

  return { noticePayment: Math.round(noticePayment * 100) / 100, noticeDays }
}

/**
 * Calcula o FGTS (8% do salário)
 */
export const calculateFGTS = (salario: number): number => {
  return Math.round((salario * 0.08) * 100) / 100
}

/**
 * Calcula o 13º salário proporcional
 */
export const calculateDecimoTerceiroProporcional = (
  salario: number,
  dataInicio: string,
  dataRescisao: string
): number => {
  if (!dataRescisao || !dataInicio) return 0

  const inicioDate = parse(dataInicio.substring(0, 10), 'yyyy-MM-dd', new Date())
  const rescisaoDate = parse(dataRescisao, 'dd/MM/yyyy', new Date())

  if (!isValid(inicioDate) || !isValid(rescisaoDate)) return 0

  // Calcula meses trabalhados no ano
  const mesesTrabalhados = differenceInMonths(rescisaoDate, inicioDate) + 1
  if (mesesTrabalhados < 0) return 0

  // 13º proporcional = (salário / 12) * meses trabalhados
  const decimoTerceiro = (salario / 12) * Math.min(mesesTrabalhados, 12)

  return Math.round(decimoTerceiro * 100) / 100
}

/**
 * Calcula férias proporcionais
 */
export const calculateFeriasProporcionais = (
  salario: number,
  dataInicio: string,
  dataRescisao: string
): number => {
  if (!dataRescisao || !dataInicio) return 0

  const inicioDate = parse(dataInicio.substring(0, 10), 'yyyy-MM-dd', new Date())
  const rescisaoDate = parse(dataRescisao, 'dd/MM/yyyy', new Date())

  if (!isValid(inicioDate) || !isValid(rescisaoDate)) return 0

  // Calcula meses trabalhados
  const mesesTrabalhados = differenceInMonths(rescisaoDate, inicioDate) + 1
  if (mesesTrabalhados < 0) return 0

  // Férias proporcionais = (salário + 1/3) / 12 * meses trabalhados
  const ferias = (salario + salario / 3) / 12 * Math.min(mesesTrabalhados, 12)

  return Math.round(ferias * 100) / 100
}

/**
 * Calcula o resumo financeiro completo
 */
export const calculateFinancialSummary = (params: CalculationParams): FinancialSummary => {
  const {
    salario,
    dataInicio,
    dataRescisao,
    tipoAviso
  } = params

  if (!dataRescisao || !tipoAviso) {
    return {
      salarioProporcional: 0,
      avisoPrevio: 0,
      fgts: 0,
      decimoTerceiroProporcional: 0,
      feriasProporcionais: 0,
      total: 0
    }
  }

  const salarioProporcional = calculateProportionalSalary(salario, dataInicio, dataRescisao)
  
  const { noticePayment: avisoPrevio } = calculateAvisoPrevio(salario, dataInicio, dataRescisao, tipoAviso)
  const fgts = calculateFGTS(salario)
  const decimoTerceiroProporcional = calculateDecimoTerceiroProporcional(salario, dataInicio, dataRescisao)
  const feriasProporcionais = calculateFeriasProporcionais(salario, dataInicio, dataRescisao)

  const total = salarioProporcional + avisoPrevio + fgts + decimoTerceiroProporcional + feriasProporcionais

  return {
    salarioProporcional,
    avisoPrevio,
    fgts,
    decimoTerceiroProporcional,
    feriasProporcionais,
    total: Math.round(total * 100) / 100
  }
}
